import { fetchOrdersHistory } from "../webService/webService";
import { roundKes } from "../orderCost";

/**
 * First-order discount — 30% off subtotal, capped at KES 250.
 *
 * Eligibility ("has this user ordered before?") is re-derived from order
 * history rather than tracked as separate redemption state: once a user's
 * first order completes, isFirstTimeCustomer() naturally returns false on
 * every later checkout, so there's no separate "used" flag to persist.
 *
 * This is an optimistic preview for the checkout UI — the amount actually
 * charged is enforced server-side in mzito-pay (discount.service.ts) using
 * the same rate/cap, independently of whatever this module computes.
 */

export const FIRST_ORDER_DISCOUNT_RATE = 0.3; // 30% off subtotal
export const FIRST_ORDER_DISCOUNT_CAP = 250; // KES

/**
 * @param {string} uid
 * @param {string} token
 * @returns {Promise<boolean>}
 */
export async function isFirstTimeCustomer(uid, token) {
  if (!uid || !token) return false;
  try {
    const history = await fetchOrdersHistory(uid, token);
    return Array.isArray(history) && history.length === 0;
  } catch (error) {
    console.error("Error checking first-order eligibility:", error);
    return false; // fail closed — no discount shown if history can't be verified
  }
}

/**
 * @param {number} subtotal
 * @param {boolean} isFirstOrder
 * @returns {{ discount: number, isFirstOrder: boolean }}
 */
export function applyFirstOrderDiscount(subtotal, isFirstOrder) {
  if (!isFirstOrder || !(subtotal > 0)) {
    return { discount: 0, isFirstOrder: false };
  }
  return {
    discount: roundKes(Math.min(subtotal * FIRST_ORDER_DISCOUNT_RATE, FIRST_ORDER_DISCOUNT_CAP)),
    isFirstOrder: true,
  };
}
